"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { SmartButton, ButtonProps } from "@/components/ui/smart-button";
import Image from "next/image";
import { StaticImageData } from "next/image";
import { scrollToSellWatchForm } from "@/lib/scroll-utils";
import { useDialog } from "@/contexts/dialog-context";

import { ChevronRight } from "lucide-react";

type ImageProps = {
  src: string | StaticImageData;
  alt?: string;
};

type TabProps = {
  heading: string;
  description: string;
  image?: ImageProps;
};

type Props = {
  tagline: string;
  heading: string;
  description: string;
  tabs: TabProps[];
  buttons: ButtonProps[];
};

export type PainPointsProps = React.ComponentPropsWithoutRef<"section"> & Partial<Props>;

//Images 
import WatchStress from "@images/landing/pain-points/1.webp";
import WatchPayment from "@images/landing/pain-points/2.webp";
import WatchService from "@images/landing/pain-points/3.webp";
import { SectionTitle } from "@/components/ui/section-title";
export default function PainPoints(props: PainPointsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { openDialog } = useDialog()
  
  const handleFormCTAClick = () => {
    scrollToSellWatchForm(() => openDialog())
  }
  
  const {
    tagline = "We Get It",
    heading = "Selling Your Luxury Watch Shouldn't Be Stressful",
    description = "Are you tired of the hassle that comes with selling your luxury timepiece? From complicated processes to unreliable dealers, the experience can be overwhelming.",
    tabs = [
      {
        heading: "Slow & Complicated Process",
        description:
          "Traditional watch selling involves endless paperwork, multiple appointments, and weeks of back-and-forth negotiations. Our streamlined digital process takes minutes, not months—meaning you get paid faster with complete transparency.",
        image: {
          src: WatchStress,
          alt: "Frustrated seller dealing with complicated watch selling process",
        },
      },
      {
        heading: "Delayed Payments & Uncertainty",
        description:
          "Waiting weeks for payment after consignment or auction creates unnecessary stress and uncertainty about your valuable timepiece. We provide instant payment upon agreement and transparent communication throughout, ensuring you know exactly when your watch is sold and payment is transferred.",
        image: {
          src: WatchPayment,
          alt: "Secure and instant payment for luxury watches",
        },
      },
      {
        heading: "Poor Communication & Service",
        description:
          "Unresponsive dealers and slow auction houses leave you in the dark about your valuable timepiece. We guarantee rapid response within hours, provide regular updates, and handle all documentation securely. Our CCTV-monitored facility ensures your watch is protected until final sale.",
        image: {
          src: WatchService,
          alt: "Professional watch service and communication",
        },
      },
    ],
    buttons = [
      { 
        title: "Get a valuation", 
        variant: "default", 
        onClick: handleFormCTAClick
      },
      {
        title: "Get in touch with us",
        variant: "link",
        size: "sm",
        iconRight: <ChevronRight className="ml-1 h-4 w-4" />,
        sectionId: "process",
        href:"/contact"
      },
    ],
    className,
    ...restProps
  } = props;

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
    <section className={cn("px-[5%] py-16 lg:py-24 xl:py-28 bg-background", className)} {...restProps}>
      <div className="sm:container mx-auto">
        <div className="relative flex flex-col lg:flex-row lg:min-h-[600px] lg:gap-16">
          <div className="w-full lg:w-1/2 lg:pr-6 xl:pr-10 flex flex-col lg:min-h-full">
            <div className="mb-8 lg:hidden">
              <p className="mb-3 font-semibold lg:mb-4">{tagline}</p>
              <h2 className="mb-5 lg:mb-6 capitalize">{ heading } </h2>
              <p className="" dangerouslySetInnerHTML={{ __html: description }} />
            </div>

            {/* Static featured image for mobile */}
            <div className="relative mb-6 h-64 md:h-96 w-full overflow-hidden rounded-md flex lg:hidden">
              <Image
                src={WatchPayment} // Using the first image as static hero image for mobile
                alt="Selling your luxury watch without hassle"
                className="h-full w-full object-cover"
                height={1000}
                width={1000}
                priority={true} // Load this image immediately as it's above the fold
              />
            </div>

            <div className="h-full hidden lg:flex lg:absolute lg:inset-y-0 lg:left-0 lg:w-1/2 lg:pr-6 xl:pr-10">
              <AnimatePresence mode="wait" initial={false}>
                {tabs.map((tab, index) => {
                  if (activeTab !== index) return null;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0 }}
                      exit={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                      className="relative flex flex-1 h-[30rem] lg:h-full w-full items-center justify-center"
                      style={{
                        willChange: 'opacity',
                        transform: 'translate3d(0, 0, 0)'
                      }}
                    >
                      {tab.image && (
                        <div className="relative max-h-[30rem] lg:max-h-full h-full w-full lg:mb-0 mb-6 overflow-hidden rounded-md">
                          <Image
                            src={tab.image.src}
                            alt={tab.image.alt || "Tab image"}
                            className="h-full w-full object-cover lg:object-center lg:-mt-0"
                            height={900}
                            width={1600}
                            sizes="(max-width: 1023px) 100vw, 50vw"
                            quality={85}
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
          <div className="w-full lg:w-1/2 lg:pl-6 xl:pl-10">
            <div className="mb-8 hidden lg:block">
              <SectionTitle theme="dark">{tagline}</SectionTitle>
              <h2 className="font-medium" dangerouslySetInnerHTML={{ __html: heading }} />
              <p className="text-base pt-2 text-balance" dangerouslySetInnerHTML={{ __html: description }} />
            </div>
            <div className="static flex flex-col flex-wrap justify-stretch lg:block">
              <Accordion 
                type="single" 
                value={activeTab.toString()} 
                onValueChange={(value) => setActiveTab(value ? parseInt(value) : 0)}
                className="relative"
                defaultValue="0"
                collapsible
              >
                {tabs.map((tab, index) => (
                  <AccordionItem
                    key={index}
                    value={index.toString()}
                    className="border-b border-gray-200"
                  >
                    <AccordionTrigger className="hover:text-primary data-[state=open]:text-primary text-foreground opacity-70 data-[state=open]:opacity-100">
                      <h4 className="">
                        {tab.heading}
                      </h4>
                    </AccordionTrigger>
                    <AccordionContent className="text-foreground">
                      <div dangerouslySetInnerHTML={{ __html: tab.description }} />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 lg:mt-8">
              {buttons.map((button, index) => (
                <SmartButton key={index} {...button} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}


